import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Tabs,
  Tab,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import auth from '../config/firebase';

const LoginDialogMulti = ({ open, onClose }) => {
  const [loginMethod, setLoginMethod] = useState(0); // 0: Email/Password, 1: Phone OTP
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [step, setStep] = useState(1); // 1: Input, 2: OTP verify, 3: Name input
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);

  const { login } = useAuth();

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setPhoneNumber('');
    setOtp('');
    setFirstName('');
    setLastName('');
    setStep(1);
    setError('');
    setSuccess('');
    setLoginMethod(0);
    if (recaptchaVerifier) {
      recaptchaVerifier.clear();
      setRecaptchaVerifier(null);
    }
    onClose();
  };

  // Email/Password Login
  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/login/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: email,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success) {
        login(data.data.accessToken, data.data.refreshToken, data.data.user);
        setSuccess('Login successful!');
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error(data.message || 'Login failed');
      }

    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initialize reCAPTCHA
  const setupRecaptcha = () => {
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container-multi', {
        size: 'normal',
        callback: (response) => {
          console.log('✅ reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.warn('⚠️  reCAPTCHA expired');
          setError('reCAPTCHA expired. Please try again.');
        }
      });
      
      setRecaptchaVerifier(verifier);
      return verifier;
    }
    return recaptchaVerifier;
  };

  // Format phone number to E.164 format
  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If exactly 10 digits, add India country code
    if (cleaned.length === 10) {
      cleaned = '91' + cleaned;
    }
    // If 12 digits and starts with 91, it already has country code
    // If other lengths, use as-is (might be invalid but let Firebase validate)
    
    return '+' + cleaned;
  };

  // Send OTP via Firebase
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!auth) {
      setError('Firebase authentication not initialized');
      return;
    }

    setLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const verifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      
      setConfirmationResult(result);
      setStep(2);
      setSuccess('OTP sent successfully! Please check your phone.');
    } catch (error) {
      setError(error.message || 'Failed to send OTP. Please try again.');
      
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and Login
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await confirmationResult.confirm(otp);
      const user = userCredential.user;
      const idToken = await user.getIdToken();

      const response = await fetch('/api/auth/firebase/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken,
          phoneNumber: user.phoneNumber
        })
      });

      const data = await response.json();

      if (data.requiresRegistration) {
        setFirebaseUser(user);
        setStep(3);
        setSuccess('Please provide your name to complete registration');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success) {
        login(data.data.accessToken, data.data.refreshToken, data.data.user);
        setSuccess('Login successful!');
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error(data.message || 'Login failed');
      }

    } catch (error) {
      let errorMessage = 'Invalid OTP. ';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage += 'Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage += 'The code has expired. Please request a new one.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Complete registration with name
  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstName || !lastName) {
      setError('Please enter both first name and last name');
      return;
    }

    setLoading(true);

    try {
      const idToken = await firebaseUser.getIdToken();

      const response = await fetch('/api/auth/firebase/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken,
          phoneNumber: firebaseUser.phoneNumber,
          firstName: firstName.trim(),
          lastName: lastName.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      if (data.success) {
        login(data.data.accessToken, data.data.refreshToken, data.data.user);
        setSuccess('Registration successful!');
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error(data.message || 'Registration failed');
      }

    } catch (error) {
      setError(error.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">🕉️ Login to Vedam Exams</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {step === 1 && (
          <>
            <Tabs 
              value={loginMethod} 
              onChange={(e, newValue) => setLoginMethod(newValue)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<EmailIcon />} label="Email & Password" />
              <Tab icon={<PhoneIcon />} label="Phone OTP" />
            </Tabs>

            {loginMethod === 0 ? (
              // Email/Password Login
              <Box component="form" onSubmit={handleEmailPasswordLogin}>
                <TextField
                  fullWidth
                  label="Email or Phone"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Login'}
                </Button>
              </Box>
            ) : (
              // Phone OTP Login
              <Box component="form" onSubmit={handleSendOTP}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  placeholder="10-digit mobile number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Enter your 10-digit mobile number"
                />

                <Box id="recaptcha-container-multi" sx={{ mt: 2, mb: 2 }}></Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send OTP'}
                </Button>
              </Box>
            )}
          </>
        )}

        {step === 2 && (
          <Box component="form" onSubmit={handleVerifyOTP}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              OTP sent to {phoneNumber}
            </Typography>

            <TextField
              fullWidth
              label="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              margin="normal"
              required
              autoFocus
              placeholder="6-digit OTP"
              inputProps={{ maxLength: 6 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify & Login'}
            </Button>

            <Button
              variant="text"
              fullWidth
              onClick={() => setStep(1)}
            >
              Back
            </Button>
          </Box>
        )}

        {step === 3 && (
          <Box component="form" onSubmit={handleCompleteRegistration}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Complete your registration
            </Typography>

            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="normal"
              required
              autoFocus
            />

            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="normal"
              required
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialogMulti;
